import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import SeoHead, { SITE_URL } from "@/components/public/SeoHead";
import { useReviewStats } from "@/hooks/useReviewStats";
import Breadcrumb from "@/components/public/Breadcrumb";
import NotFoundPage from "@/pages/public/NotFoundPage";
import { VITRINE_SLUGS, getVitrineContent } from "./cityData";
import FadeIn from "@/components/public/FadeIn";
import CTASection from "@/components/public/CTASection";
import GoogleReviewCard from "@/components/public/GoogleReviewCard";
import DevisForm from "@/components/public/DevisForm";
import { Loader2, MapPin, ArrowRight, Star, CheckCircle, Quote, BookOpen, ClipboardList, ChevronDown, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SeoPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [avis, setAvis] = useState([]);
  const [allSeoPages, setAllSeoPages] = useState([]);
  const [blogArticle, setBlogArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const reviewStats = useReviewStats();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [p, a, all] = await Promise.all([
          axios.get(`${API}/pages-seo/${slug}`),
          axios.get(`${API}/avis`),
          axios.get(`${API}/pages-seo`),
        ]);
        setPage(p.data);
        setAvis(a.data);
        setAllSeoPages(all.data);
        try {
          const blog = await axios.get(`${API}/blog?service=${encodeURIComponent(p.data.service_name)}&ville=${encodeURIComponent(p.data.ville_name)}&limit=1`);
          const articles = blog.data.articles || [];
          setBlogArticle(articles[0] || null);
        } catch { setBlogArticle(null); }
      } catch {
        setPage(null);
      }
      setLoading(false);
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex justify-center py-32">
        <Loader2 className="w-8 h-8 animate-spin text-[#336DBF]" />
      </div>
    );
  }

  if (!page) return <NotFoundPage />;

  const c = page.copywriting || {};
  const villeName = page.ville_name;
  const serviceName = page.service_name;

  const villeAvis = avis.filter(a => a.ville === villeName);
  const displayAvis = avis.slice(0, 3);
  const avgRating = avis.length ? (avis.reduce((s, a) => s + a.note, 0) / avis.length).toFixed(1) : "4.9";

  const sameVille = allSeoPages.filter(p => p.ville_name === villeName && p.slug !== slug && p.is_active);
  const sameService = allSeoPages.filter(p => p.service_name === serviceName && p.slug !== slug && p.is_active);

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": serviceName,
    "provider": { "@type": "LocalBusiness", "@id": "https://www.nettoyage-expert.ch/#business" },
    "areaServed": { "@type": "City", "name": villeName },
    "description": `${serviceName} professionnel à ${villeName} par Nettoyage Expert, noté 5 étoiles sur Google.`
  };

  const serviceSlugPart = page.slug?.replace(/-[^-]+$/, "") || "";
  const isVitrine = VITRINE_SLUGS.has(page.slug);
  const vitrineData = isVitrine ? getVitrineContent(page.slug, serviceName, villeName) : null;

  const breadcrumbItems = [
    { name: "Accueil", url: "/" },
    { name: "Services", url: "/services" },
    { name: serviceName, url: `/services/${serviceSlugPart}` },
    { name: `${villeName}` }
  ];

  const allSchemas = [serviceSchema];
  if (vitrineData?.faq?.length) {
    allSchemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": vitrineData.faq.map(f => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.r }
      }))
    });
  }

  return (
    <div data-testid="seo-page">
      <SeoHead
        title={`${serviceName} ${villeName} – dès 125 CHF`}
        description={`${serviceName} professionnel à ${villeName} dès 125 CHF. Résultat immédiat. 5★ Google, ${reviewStats.count} avis. Devis gratuit en 2h. Nettoyage Expert.`}
        path={`/${page.slug}`}
        noindex={!isVitrine}
        breadcrumbs={breadcrumbItems}
        schemas={allSchemas}
      />
      <Breadcrumb items={breadcrumbItems} />

      {/* HERO */}
      <section className="bg-[#336DBF] py-16 sm:py-20" data-testid="seo-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-1.5 mb-5">
            <Star size={14} className="fill-amber-400 text-amber-400" />
            <span className="text-white/90 text-sm font-medium">5/5 &bull; Recommandé à {villeName}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight" data-testid="seo-h1">
            {serviceName} à {villeName}
          </h1>
          {c.hook && <p className="mt-4 text-lg sm:text-xl text-white/85 max-w-2xl">{c.hook}</p>}
          <Link
            to="/contact"
            className="mt-6 inline-flex items-center justify-center px-8 py-4 bg-[#336DBF] text-white font-semibold rounded-md hover:bg-[#2A5A9E] transition-colors text-base sm:text-lg"
            data-testid="seo-hero-cta"
          >
            Devis gratuit
          </Link>
        </div>
      </section>

      {/* PROBLEME */}
      {c.problème && (
        <section className="py-16 sm:py-24 bg-white" data-testid="seo-probleme">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Le problème</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-4">
                Pourquoi agir maintenant à {villeName} ?
              </h2>
              <p className="text-[#062133]/60 text-lg leading-relaxed">{c.problème}</p>
              {c.agitation && <p className="text-[#062133] text-lg leading-relaxed mt-4 font-medium">{c.agitation}</p>}
            </FadeIn>
          </div>
        </section>
      )}

      {/* SOLUTION */}
      {c.solution && (
        <section className="py-16 sm:py-24 bg-[#F4F6F8]" data-testid="seo-solution">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Notre solution</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-4">
                Notre solution à {villeName}
              </h2>
              <p className="text-[#062133]/60 text-lg leading-relaxed">{c.solution}</p>
            </FadeIn>
          </div>
        </section>
      )}

      <CTASection title={c.cta || `Demandez votre devis gratuit pour ${serviceName} à ${villeName}`} />

      {/* VITRINE: DEROULEMENT DE L'INTERVENTION */}
      {vitrineData?.deroulement && (
        <section className="py-16 sm:py-24 bg-white" data-testid="vitrine-deroulement">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Étape par étape</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-3">
                Déroulement de l'intervention à {villeName}
              </h2>
              {vitrineData.duree && (
                <p className="text-[#062133]/50 text-sm mb-8">Durée estimée : {vitrineData.duree}</p>
              )}
            </FadeIn>
            <div className="space-y-6">
              {vitrineData.deroulement.map((etape, i) => (
                <FadeIn key={i} delay={i * 100}>
                  <div className="flex gap-4 sm:gap-6 items-start">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#336DBF] text-white flex items-center justify-center font-bold text-sm">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-[#062133] mb-1">{etape.titre}</h3>
                      <p className="text-[#062133]/60 leading-relaxed">{etape.desc}</p>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* VITRINE: POURQUOI CHOISIR NETTOYAGE EXPERT */}
      {vitrineData?.pourquoi && (
        <section className="py-16 sm:py-24 bg-[#F4F6F8]" data-testid="vitrine-pourquoi">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Nos avantages</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-6">
                Pourquoi choisir Nettoyage Expert à {villeName} ?
              </h2>
              <div className="bg-white border border-[#062133]/10 rounded-lg p-6 sm:p-8">
                <div className="flex gap-4 items-start">
                  <Shield className="w-6 h-6 text-[#336DBF] shrink-0 mt-0.5" />
                  <p className="text-[#062133]/70 text-lg leading-relaxed">{vitrineData.pourquoi}</p>
                </div>
              </div>
            </FadeIn>
          </div>
        </section>
      )}

      {/* VITRINE: FAQ ETENDUE */}
      {vitrineData?.faq?.length > 0 && (
        <section className="py-16 sm:py-24 bg-white" data-testid="vitrine-faq">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Questions fréquentes</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-8">
                FAQ — {serviceName} à {villeName}
              </h2>
            </FadeIn>
            <div className="space-y-4">
              {vitrineData.faq.map((item, i) => (
                <FadeIn key={i} delay={i * 80}>
                  <details className="group bg-[#F4F6F8] border border-[#062133]/10 rounded-lg" data-testid={`vitrine-faq-item-${i}`}>
                    <summary className="flex items-center justify-between cursor-pointer p-5 sm:p-6 list-none [&::-webkit-details-marker]:hidden">
                      <span className="text-[#062133] font-medium pr-4">{item.q}</span>
                      <ChevronDown className="w-5 h-5 text-[#336DBF] shrink-0 transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="px-5 sm:px-6 pb-5 sm:pb-6 pt-0">
                      <p className="text-[#062133]/60 leading-relaxed">{item.r}</p>
                    </div>
                  </details>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* POURQUOI RECOMMANDE */}
      {c.pourquoi_recommandé && (
        <section className="py-16 sm:py-24 bg-white" data-testid="seo-pourquoi">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Confiance</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-4">
                Pourquoi nous sommes recommandés à {villeName}
              </h2>
              <div className="flex gap-4 items-start">
                <CheckCircle className="w-6 h-6 text-[#336DBF] shrink-0 mt-1" />
                <p className="text-[#062133]/60 text-lg leading-relaxed">{c.pourquoi_recommandé}</p>
              </div>
              {c.preuve && (
                <div className="flex gap-4 items-start mt-4">
                  <CheckCircle className="w-6 h-6 text-[#336DBF] shrink-0 mt-1" />
                  <p className="text-[#062133]/60 text-lg leading-relaxed">{c.preuve}</p>
                </div>
              )}
            </FadeIn>
          </div>
        </section>
      )}

      {/* ZONE D'INTERVENTION */}
      {page.zone_detail && (
        <section className="py-16 sm:py-24 bg-[#F4F6F8]" data-testid="seo-zones">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Zones d'intervention</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-4">
                Intervention à {villeName} et ses environs
              </h2>
              <div className="flex items-start gap-3">
                <MapPin className="w-6 h-6 text-[#336DBF] shrink-0 mt-1" />
                <p className="text-[#062133]/60 text-lg leading-relaxed">{page.zone_detail}</p>
              </div>
            </FadeIn>
          </div>
        </section>
      )}

      <CTASection title={`Plus de 500 interventions en Suisse romande`} subtitle={`Résultat garanti pour votre ${serviceName.toLowerCase()} à ${villeName}`} buttonText="Obtenir mon devis gratuit" />

      {/* CAS CONCRET */}
      {c.cas_concret && (
        <section className="py-16 sm:py-24 bg-white" data-testid="seo-cas-concret">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Témoignage</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-6">
                Témoignage client à {villeName}
              </h2>
              <div className="bg-[#F4F6F8] border border-[#062133]/10 rounded-lg p-6 sm:p-8">
                <Quote className="w-8 h-8 text-[#336DBF]/30 mb-3" />
                <p className="text-[#062133] text-lg leading-relaxed italic">{c.cas_concret}</p>
              </div>
            </FadeIn>
          </div>
        </section>
      )}

      {/* AVIS GOOGLE */}
      <section className="py-16 sm:py-24 bg-[#F4F6F8]" data-testid="seo-avis">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Avis clients</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-8">
              Ce que disent nos clients sur Google
            </h2>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {displayAvis.map((a, i) => (
              <FadeIn key={a.id || i} delay={i * 80}>
                <GoogleReviewCard avis={a} index={i} />
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* PREUVE LOCALE */}
      {c.preuve_locale && (
        <section className="py-12 sm:py-16 bg-white" data-testid="seo-preuve-locale">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <FadeIn>
              <p className="text-[#336DBF] font-semibold text-lg">{c.preuve_locale}</p>
            </FadeIn>
          </div>
        </section>
      )}

      {/* Blog article link */}
      {blogArticle && (
        <section className="py-10 sm:py-14 bg-[#F4F6F8]" data-testid="seo-blog-link">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <Link
                to={`/blog/${blogArticle.slug}`}
                className="flex items-center gap-4 bg-white border border-[#062133]/10 rounded-lg p-5 hover:border-[#336DBF]/30 hover:shadow-sm transition-all"
              >
                <BookOpen className="w-8 h-8 text-[#336DBF] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#336DBF] mb-1">En savoir plus</p>
                  <p className="text-sm font-semibold text-[#062133] truncate">{blogArticle.title}</p>
                </div>
                <ArrowRight size={16} className="text-[#336DBF] shrink-0" />
              </Link>
            </FadeIn>
          </div>
        </section>
      )}

      {/* INTERNAL LINKS — SAME VILLE */}
      {sameVille.length > 0 && (
        <section className="py-16 sm:py-24 bg-[#F4F6F8]" data-testid="seo-autres-services">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Nos services</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-6">
                Autres services à {villeName}
              </h2>
            </FadeIn>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {sameVille.map((p, i) => (
                <FadeIn key={p.slug} delay={i * 50}>
                  <Link
                    to={`/${p.slug}`}
                    className="block bg-white border border-[#062133]/10 rounded-lg p-4 hover:border-[#336DBF]/30 hover:shadow-sm transition-all"
                    data-testid={`link-${p.slug}`}
                  >
                    <span className="text-sm font-medium text-[#062133] hover:text-[#336DBF]">{p.service_name}</span>
                    <span className="block text-xs text-[#062133]/60 mt-1">à {p.ville_name}</span>
                  </Link>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* INTERNAL LINKS — SAME SERVICE */}
      {sameService.length > 0 && (
        <section className="py-16 sm:py-24 bg-white" data-testid="seo-autres-villes">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Autres villes</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-6">
                {serviceName} dans d'autres villes
              </h2>
            </FadeIn>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {sameService.map((p, i) => (
                <FadeIn key={p.slug} delay={i * 50}>
                  <Link
                    to={`/${p.slug}`}
                    className="block bg-[#F4F6F8] border border-[#062133]/10 rounded-lg p-4 hover:border-[#336DBF]/30 hover:shadow-sm transition-all"
                    data-testid={`link-${p.slug}`}
                  >
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-[#336DBF]" />
                      <span className="text-sm font-medium text-[#062133]">{p.ville_name}</span>
                    </div>
                  </Link>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DEVIS FORM */}
      <section className="py-16 sm:py-24 bg-[#F4F6F8]" id="devis-section" data-testid="seo-devis-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-8">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Contact</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight">
                Demandez votre devis pour {serviceName} à {villeName}
              </h2>
              <p className="mt-2 text-[#062133]/60">Réponse garantie sous 2 heures</p>
            </div>
            <div className="bg-white border border-[#062133]/10 rounded-lg p-6 sm:p-8 shadow-sm">
              <DevisForm source="seo" prefilledService={serviceName} prefilledVille={villeName} />
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
